const EVENTS_ARCHIVE_ROOT_SELECTOR = '[data-js="tribe-events-view"]';
const LOAD_MORE_LINK_SELECTOR = 'a.tribe-events-c-nav__next[rel="next"]';
const LIST_APPEND_TARGET_SELECTOR = ".tribe-events-calendar-list";
const PHOTO_APPEND_TARGET_SELECTOR = ".tribe-events-pro-photo .tribe-common-g-row";
const APPEND_TARGET_SELECTOR = `${LIST_APPEND_TARGET_SELECTOR}, ${PHOTO_APPEND_TARGET_SELECTOR}`;
const LOADING_CLASS = "is-loading-more-events";
const NONCE_DATA_SELECTOR = "script[data-js='tribe-events-view-nonce-data']";
const CACHED_NONCE_DATA = { tvn1: "", tvn2: "" };

export const initEventArchiveAjax = () => {
	const config = getEventArchiveAjaxConfig();
	if (!config.ajaxUrl || !config.action || !config.nonce) return;

	primeNonceDataCache();

	const roots = document.querySelectorAll(EVENTS_ARCHIVE_ROOT_SELECTOR);
	if (!roots.length) return;

	roots.forEach((root) => {
		root.setAttribute("data-pp-ajax-url", config.ajaxUrl);
		root.setAttribute("data-pp-ajax-action", config.action);
		root.setAttribute("data-pp-ajax-nonce", config.nonce);
	});

	document.addEventListener(
		"click",
		(event) => {
			const eventTarget = event.target instanceof Element ? event.target : event.target?.parentElement;
			if (!eventTarget) return;

			const loadMoreLink = eventTarget.closest(LOAD_MORE_LINK_SELECTOR);
			if (!loadMoreLink) return;

			const root = loadMoreLink.closest(EVENTS_ARCHIVE_ROOT_SELECTOR);
			if (!root) return;

			const viewType = getEventsViewType(root);
			if (!viewType) return;

			const restRequestData = getRestRequestData(root);

			event.preventDefault();
			event.stopPropagation();
			loadMoreEvents(root, config, loadMoreLink, viewType, restRequestData);
		},
		true,
	);
};

const getEventsViewType = (root) => {
	if (root.querySelector(LIST_APPEND_TARGET_SELECTOR)) return "list";
	if (root.querySelector(PHOTO_APPEND_TARGET_SELECTOR)) return "photo";

	return "";
};

const getRestRequestData = (root) => {
	const restUrl = `${root.getAttribute("data-view-rest-url") || ""}`.trim();
	const nonceData = resolveNonceData(root);

	return {
		restUrl,
		tvn1: nonceData.tvn1,
		tvn2: nonceData.tvn2,
	};
};

const resolveNonceData = (root) => {
	const scripts = findNonceScripts(root);
	let fallbackData = CACHED_NONCE_DATA.tvn1 ? { ...CACHED_NONCE_DATA } : { tvn1: "", tvn2: "" };

	for (const script of scripts) {
		const nonceData = parseNonceData(script.textContent || "");
		if (nonceData.tvn1) {
			cacheNonceData(nonceData);
			return nonceData;
		}

		if (nonceData.tvn2 || !fallbackData.tvn1) {
			fallbackData = nonceData;
		}
	}

	if (fallbackData.tvn1) {
		cacheNonceData(fallbackData);
		return fallbackData;
	}

	const managerNonceData = getManagerNonceData();
	if (managerNonceData.tvn1 || managerNonceData.tvn2) {
		cacheNonceData(managerNonceData);
		return managerNonceData;
	}

	return fallbackData;
};

const cacheNonceData = (nonceData) => {
	if (nonceData.tvn1) {
		CACHED_NONCE_DATA.tvn1 = nonceData.tvn1;
	}

	if (nonceData.tvn2) {
		CACHED_NONCE_DATA.tvn2 = nonceData.tvn2;
	}
};

const primeNonceDataCache = () => {
	if (CACHED_NONCE_DATA.tvn1) {
		return;
	}

	const nonceScripts = document.querySelectorAll(NONCE_DATA_SELECTOR);
	for (const script of nonceScripts) {
		const nonceData = parseNonceData(script.textContent || "");
		if (nonceData.tvn1 || nonceData.tvn2) {
			cacheNonceData(nonceData);
			if (nonceData.tvn1) {
				return;
			}
		}
	}

	cacheNonceData(getManagerNonceData());
};

const getManagerNonceData = () => {
	const managerNonces = window?.tribe?.events?.views?.manager?.nonces || {};

	return {
		tvn1: `${managerNonces?.tvn1 || ""}`.trim(),
		tvn2: `${managerNonces?.tvn2 || ""}`.trim(),
	};
};

const findNonceScripts = (root) => {
	const scripts = [];
	const seen = new Set();

	const addScript = (script) => {
		if (!script || seen.has(script)) return;
		seen.add(script);
		scripts.push(script);
	};

	root.querySelectorAll(NONCE_DATA_SELECTOR).forEach(addScript);

	let current = root.parentElement;
	while (current) {
		current.querySelectorAll(NONCE_DATA_SELECTOR).forEach(addScript);

		current = current.parentElement;
	}

	document.querySelectorAll(NONCE_DATA_SELECTOR).forEach(addScript);

	return scripts;
};

const parseNonceData = (scriptContent) => {
	const raw = `${scriptContent || ""}`.trim();
	if (!raw) {
		return { tvn1: "", tvn2: "" };
	}

	try {
		const nonceData = JSON.parse(raw);

		return {
			tvn1: `${nonceData?.tvn1 || ""}`.trim(),
			tvn2: `${nonceData?.tvn2 || ""}`.trim(),
		};
	} catch {
		const tvn1Match = raw.match(/"tvn1"\s*:\s*"([^"]*)"/);
		const tvn2Match = raw.match(/"tvn2"\s*:\s*"([^"]*)"/);

		return {
			tvn1: `${tvn1Match?.[1] || ""}`.trim(),
			tvn2: `${tvn2Match?.[1] || ""}`.trim(),
		};
	}
};

const getEventArchiveAjaxConfig = () => {
	const appConfig = window.posturepressApp || {};
	const archiveConfig = appConfig.eventsArchiveAjax || {};

	return {
		ajaxUrl: `${appConfig.ajaxUrl || ""}`.trim(),
		action: `${archiveConfig.action || ""}`.trim(),
		nonce: `${archiveConfig.nonce || ""}`.trim(),
	};
};

const loadMoreEvents = async (root, config, loadMoreLink, viewType, restRequestData) => {
	if (root.classList.contains(LOADING_CLASS)) return;

	const nextUrl = `${loadMoreLink.getAttribute("href") || ""}`.trim();
	if (!nextUrl) return;

	root.classList.add(LOADING_CLASS);
	loadMoreLink.setAttribute("aria-disabled", "true");

	try {
		const body = new URLSearchParams({
			action: config.action,
			nonce: config.nonce,
			nextUrl,
			viewType,
			restUrl: restRequestData.restUrl,
			tvn1: restRequestData.tvn1,
			tvn2: restRequestData.tvn2,
		});

		const response = await fetch(config.ajaxUrl, {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
			},
			credentials: "same-origin",
			body: body.toString(),
		});

		if (!response.ok) {
			throw new Error(`HTTP ${response.status}`);
		}

		const payload = await response.json();
		if (!payload?.success) {
			throw new Error("Ajax request failed");
		}

		const itemsHtml = `${payload?.data?.itemsHtml || ""}`.trim();
		const resolvedNextUrl = `${payload?.data?.nextUrl || ""}`.trim();

		if (itemsHtml) {
			appendItems(root, itemsHtml);
		}

		updateLoadMoreLink(loadMoreLink, resolvedNextUrl);
	} catch {
		loadMoreLink.removeAttribute("aria-disabled");
	} finally {
		root.classList.remove(LOADING_CLASS);
	}
};

const appendItems = (root, itemsHtml) => {
	const list = root.querySelector(APPEND_TARGET_SELECTOR);
	if (!list) return;

	const template = document.createElement("template");
	template.innerHTML = itemsHtml;

	const nextItems = Array.from(template.content.children);
	if (!nextItems.length) return;

	nextItems.forEach((item) => {
		list.append(item);
	});
};

const updateLoadMoreLink = (loadMoreLink, nextUrl) => {
	if (!nextUrl) {
		const navItem = loadMoreLink.closest(".tribe-events-c-nav__list-item--next");
		if (navItem) {
			navItem.remove();
			return;
		}

		loadMoreLink.remove();
		return;
	}

	loadMoreLink.setAttribute("href", nextUrl);
	loadMoreLink.removeAttribute("aria-disabled");
};
